/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {Injectable} from '@angular/core';
import {delay, map, Observable, tap} from 'rxjs';
import {MapToolsService} from '../../services/map-tools.service';
import {AttributeViewerFilterService} from './attribute-viewer-filter.service';
import {TraitsService} from '../../../common/service/traits.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {AttributeViewerTranslationService} from './attribute-viewer-translation.service';
import {NgEventBus} from 'ng-event-bus';
import {PermissionsService} from '../../../common/service/permissions.service';
import {MapService} from '../../services/map.service';
import {FeaturesDeletionService} from '../../services/features-deletion.service';
import {MapEventsSelectionService} from '../../services/map-events-selection.service';
import {AttributeViewerEditorService} from './attribute-viewer-editor.service';
import {AttributeViewerContext} from './attribute-viewer-context.model';
import {DomSanitizer} from '@angular/platform-browser';

@Injectable({
    providedIn: 'root'
})
export class AttributeViewerService {
    title = 'AttributeViewerService';

    ctrlKeyDown = false;

    shiftKeyDown = false;

    unhighlightFeaturesOutOfContext = true;

    constructor(
        private attributeViewerEditorService: AttributeViewerEditorService,
        private attributeViewerFilterService: AttributeViewerFilterService,
        private attributeViewerTranslationService: AttributeViewerTranslationService,
        private domSanitizer: DomSanitizer,
        private featuresDeletionService: FeaturesDeletionService,
        private eventBus: NgEventBus,
        private mapEventsSelectionService: MapEventsSelectionService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private spatialUtilService: SpatialUtilService,
        private traitsService: TraitsService
    ) {}

    unhighlightFeaturesOutOfContextAllowed(allowed?: boolean) {
        if (arguments.length >= 1) {
            this.unhighlightFeaturesOutOfContext = allowed;
        }
        if (this.mapToolsService.isActive('IDENTIFY_FEATURES_WINDOW')) {
            return !(this.ctrlKeyDown || this.shiftKeyDown) && this.unhighlightFeaturesOutOfContext;
        } else {
            return !this.ctrlKeyDown && this.unhighlightFeaturesOutOfContext;
        }
    }

    reloadFeatureAttributes(context: AttributeViewerContext) {
        this.removeFeaturesByListType(context.selectedAttrsList, context.selectedAttrsList);
        context.options['enlargementMode'] = this.mapService.getMapMode().type === 'enlargement';

        return this.populateFeatures(context).pipe(
            tap(() => {
                const highlightedFeatures = this.spatialUtilService.getHighlightedFeatures(context.featureLayer);
                this.selectFeatures(highlightedFeatures, context.attrsList, context.selectedAttrsList);

                context.options.columns = this.filterColumnValuesByPlanTraitCriteria(context.options.columns);

                // Reapply filter
                this.attributeViewerFilterService.applyFilter(context);

                if (context.validate) {
                    context.validate();
                }

                this.eventBus.cast('featureTableUpdate');
            })
        );
    }

    selectFeatures(features: any[], attrsList: any[], selectedAttrsList: any[]) {
        console.debug(`${this.title} - selectFeatures`);

        const attrsIds = attrsList.map(attrs => attrs.id);
        const selectedAttrsIds = selectedAttrsList.map(attrs => attrs.id);
        const featureIds = features
            .filter(f => f.attributes)
            .map(f => f.attributes.id)
            .filter(id => attrsIds.includes(id) && !selectedAttrsIds.includes(id));

        selectedAttrsList.push(...attrsList.filter(attrs => featureIds.includes(attrs.id)));
    }

    deselectFeatures(features: any[], selectedAttrsList: any[]) {
        console.debug(`${this.title} - deselectFeatures`);

        const featureIds = features ? features.filter(f => f.attributes).map(f => f.attributes.id) : [];
        selectedAttrsList.splice(0, selectedAttrsList.length, ...selectedAttrsList.filter(attrs => !featureIds.includes(attrs.id)));
    }

    moveToFeaturesExtentCenter(context: AttributeViewerContext, attrsList: any[]) {
        console.debug(`${this.title} - moveToFeaturesExtentCenter`);

        // Used in case user is switching feature table types
        attrsList = attrsList || context.selectedAttrsList;

        const graphics = this.spatialUtilService.getGraphics(context.featureLayer);
        const selectedGraphics = attrsList.map(attrs => graphics.find((g: any) => attrs.id === g.attributes.id)).filter(g => g);

        const extent = this.mapService.getCalculatedExtentFromFeatures(selectedGraphics);
        if (extent && !this.mapToolsService.isActive('LOCK_AUTO_PAN')) {
            this.mapService.zoomToExtentCenter(extent);
        }
    }

    deleteSelectedFeatures(context: AttributeViewerContext) {
        console.debug(`${this.title} - deleteSelectedFeatures`);

        const featuresToBeDeleted = context.selectedAttrsList.reduce((acc, attrs) => {
            acc.push(...context.features.filter((feature: any) => `${attrs.id}` === `${feature.attributes.id}`));
            return acc;
        }, []);

        if (featuresToBeDeleted.length) {
            const featuresToBeDeletedIds = featuresToBeDeleted
                .map((f: any) => f.attributes.id)
                .filter((v: any, i: number, a: any[]) => i === a.indexOf(v));
            const sanitizedFeaturesToBeDeleted = this.spatialUtilService
                .getGraphics(context.featureLayer)
                .filter((g: any) => featuresToBeDeletedIds.includes(g.attributes.id));
            this.mapToolsService
                .activate('DELETE_OTHER_FEATURES', () => {
                    this.featuresDeletionService.deleteFeatures(sanitizedFeaturesToBeDeleted, context.featureLayer).subscribe();
                })
                .subscribe();
        }
    }

    removeFeatures(context: AttributeViewerContext, features: any[]) {
        console.debug(`${this.title} - removeFeatures`);

        this.removeFeaturesByListType(features, context.selectedAttrsList);
        this.removeFeaturesByListType(features, context.attrsList);
        this.removeFeaturesByListType(features, context.features);
    }

    checkSelectedFeaturesChange(context: AttributeViewerContext, newFeatures: any[], oldFeatures: any[]) {
        console.debug(`${this.title} - checkSelectedFeaturesChange - new size: ${newFeatures.length}, old size: ${oldFeatures.length}`);

        const newIds = newFeatures.map(attrs => attrs.id);
        const oldIds = oldFeatures.map(attrs => attrs.id);

        const itemsToRemove = oldIds.length ? oldFeatures.filter(attrs => !newIds.includes(attrs.id)) : [];
        if (itemsToRemove.length) {
            // Remove deselected features
            this.setFeatureHighlights(context, false, itemsToRemove, true, context.featuresAssociationActive);
        }

        if ((newFeatures.length !== oldFeatures.length || itemsToRemove.length > 0) && this.unhighlightFeaturesOutOfContextAllowed()) {
            const contextFeatureType = context.featureLayer.id.replace('FeatureLayer', '');
            const contextBusinessFeatureType = ['interest', 'lot', 'road'].find(v => v === contextFeatureType);

            this.spatialUtilService
                .getHighlightedFeatureSets()
                .filter((selectionSet: any) => context.featureLayer.id !== selectionSet.featureLayer.id && selectionSet.graphics.length)
                .forEach((selectionSet: any) => {
                    const selectionSetFeatureType = selectionSet.featureLayer.id.replace('FeatureLayer', '');
                    const featuresToUnhighlight =
                        selectionSetFeatureType !== 'polygon' || newIds.length === 0 || !contextBusinessFeatureType
                            ? selectionSet.graphics
                            : selectionSet.graphics.filter((g: any) => {
                                  const idAttr = g.attributes[`${contextBusinessFeatureType}id`];
                                  if (idAttr && newIds.includes(idAttr)) {
                                      return false;
                                  }

                                  const idsAttr = g.attributes[`${contextBusinessFeatureType}ids`];
                                  return !(idsAttr && newIds.some(newId => idsAttr.includes(`[${newId}]`)));
                              });

                    if (this.mapToolsService.isActive('IDENTIFY_FEATURES') && this.mapToolsService.isActive('IDENTIFY_FEATURES_WINDOW')) {
                        this.eventBus.cast('resetFeaturesInteracted');
                    } else {
                        this.spatialUtilService.unhighlightFeatures(selectionSet.featureLayer, featuresToUnhighlight);
                    }
                });
        }

        this.unhighlightFeaturesOutOfContextAllowed(true);

        if (newIds.length) {
            const itemsToAdd = newFeatures.filter((attrs: any) => {
                //Only add newly selected features
                return oldIds.indexOf(attrs.id) === -1;
            });

            this.setFeatureHighlights(context, true, itemsToAdd, true, context.featuresAssociationActive);

            this.moveToFeaturesExtentCenter(context, newFeatures);
        }

        if (context.options.singleSelect && context.selectedAttrsList && context.selectedAttrsList.length > 1) {
            // On add polygons mode, polygons can only be added to ONE parent feature at a time therefore only one parent should be selected
            context.selectedAttrsList.splice(1, context.selectedAttrsList.length - 1);
        }

        if (this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
            this.eventBus.cast('resetFeaturesInteracted');
        } else if (['angleFeatureLayer', 'pointFeatureLayer'].includes(context.featureLayer.id)) {
            if (context.selectedAttrsList.length === 1) {
                const selectedFeatureId = context.selectedAttrsList[0].id;
                if (
                    !this.mapEventsSelectionService.graphicToModify ||
                    !this.mapEventsSelectionService.graphicToModify.attributes ||
                    this.mapEventsSelectionService.graphicToModify.attributes.id !== selectedFeatureId
                ) {
                    this.mapEventsSelectionService.lastFeatureLayer = this.mapService[context.featureLayer.id];
                    this.mapEventsSelectionService.lastFeature = this.spatialUtilService
                        .getGraphics(this.mapEventsSelectionService.lastFeatureLayer)
                        .filter((f: any) => f.attributes.id === selectedFeatureId);
                }
            }
        }

        if (this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW')) {
            this.attributeViewerEditorService.setupEditor(context).subscribe();
        }
    }

    setFeatureHighlights(
        context: AttributeViewerContext,
        highlight: boolean,
        featureAttributesArray: any[],
        includeFeatures: boolean,
        includeChildFeatures: boolean
    ) {
        console.debug(`${this.title} - setFeatureHighlights - highlight: ${highlight}, featureAttributesArray:`, featureAttributesArray);

        const featureIds = featureAttributesArray.map(v => v.id);

        if (includeFeatures) {
            const featureType = this.spatialUtilService.getFeatureLayerName(context.featureLayer);

            const relatedAnnotations = this.spatialUtilService
                .getAnnotations()
                .filter((a: any) => a.attributes.featuretype === featureType && featureIds.includes(a.attributes.featureid));

            if (highlight) {
                this.spatialUtilService.highlightFeatures(context.featureLayer, featureIds);
                if (context.tool.selectRelatedAnnotations) {
                    this.spatialUtilService.highlightFeatures('annotation', relatedAnnotations);
                }
            } else {
                this.spatialUtilService.unhighlightFeatures(context.featureLayer, featureIds);
                if (context.tool.selectRelatedAnnotations) {
                    this.spatialUtilService.unhighlightFeatures('annotation', relatedAnnotations);
                }
            }
        }

        if (includeChildFeatures) {
            const childPolygons = [];
            const childPolylines = [];

            const contextFeatureClass = context.featureLayer.id.replace('FeatureLayer', '');
            switch (contextFeatureClass) {
                case 'interest':
                    childPolygons.push(
                        ...this.spatialUtilService
                            .getPolygons()
                            .filter(
                                (p: any) =>
                                    p.attributes.interestids &&
                                    this.spatialUtilService.parseIdsAttr(p.attributes.interestids).some(interestId => featureIds.includes(interestId))
                            )
                    );
                    childPolylines.push(
                        ...this.spatialUtilService
                            .getLines()
                            .filter(
                                (p: any) =>
                                    p.attributes.interestids &&
                                    this.spatialUtilService.parseIdsAttr(p.attributes.interestids).some(interestId => featureIds.includes(interestId))
                            )
                    );
                    break;
                case 'lot':
                    childPolygons.push(
                        ...this.spatialUtilService.getPolygons().filter((p: any) => p.attributes.lotid && featureIds.includes(p.attributes.lotid))
                    );
                    break;
                case 'road':
                    childPolygons.push(
                        ...this.spatialUtilService.getPolygons().filter((p: any) => p.attributes.roadid && featureIds.includes(p.attributes.roadid))
                    );
                    break;
            }

            if (childPolygons.length) {
                if (highlight) {
                    this.spatialUtilService.highlightFeatures('polygon', childPolygons);
                } else {
                    this.spatialUtilService.unhighlightFeatures('polygon', childPolygons);
                }
            }

            if (childPolylines.length) {
                if (highlight) {
                    this.spatialUtilService.highlightFeatures('line', childPolylines);
                } else {
                    this.spatialUtilService.unhighlightFeatures('line', childPolylines);
                }
            }
        }
    }

    openEditor(context: AttributeViewerContext, editMode: boolean) {
        console.debug(`${this.title} - openEditor`);

        if (context.tool.useDefaultEditor) {
            this.mapToolsService.toggle('ATTRIBUTE_EDITOR_WINDOW');
        } else {
            const activeTool = context.tool.type.charAt(0).toUpperCase() + context.tool.type.slice(1);
            this.eventBus.cast(`open${activeTool}Modal`, editMode ? context.selectedAttrsList[0] : null);
        }
    }

    private filterColumnValuesByPlanTraitCriteria(columns: any[]) {
        return columns.filter(l => this.traitsService.criteriaMatches(l.planTraitCriteria, true));
    }

    private populateFeatures(context: AttributeViewerContext): Observable<any> {
        console.debug(`${this.title} - populateFeatures`);

        // Translate attributes with corresponding lookup value
        return this.attributeViewerTranslationService.translateAttributeValues(context, this.getFeatures(context)).pipe(
            map((translatedFeatures: any[]) => {
                context.features = context.features || [];
                context.attrsList = context.attrsList || [];
                context.features.splice(0, context.features.length, ...translatedFeatures);
                context.attrsList.splice(0, context.attrsList.length, ...translatedFeatures.map(f => f.attributes));

                const expandedParentIds = this.attributeViewerFilterService.getExpandedParentIds(context);

                context.attrsList = context.attrsList
                    .filter(attrs => !attrs.transientParentId)
                    .reduce((acc: any[], v: any) => {
                        acc.push(
                            v,
                            ...context.attrsList.filter(
                                attrs =>
                                    attrs.transientParentId &&
                                    attrs.transientParentId === v.transientId &&
                                    expandedParentIds.includes(attrs.transientParentId)
                            )
                        );
                        return acc;
                    }, []);

                context.attrsList
                    .filter(attrs => !context.features.some(feature => feature.attributes.transientParentId === attrs.transientId))
                    .forEach(attrs => (attrs.treeStatus = 'disabled'));

                const featureAttributesList = context.attrsList;
                // Set context.attrsList undefined to force the ngx-datatable element out of the DOM.
                context.attrsList = undefined;

                // Strip $$id attributes (added by ngx-datatable) to avoid duplicate $$id attributes
                featureAttributesList.filter(attrs => Object.hasOwn(attrs, '$$id')).forEach(attrs => delete attrs.$$id);

                return featureAttributesList;
            }),
            delay(0),
            tap((featureAttributesList: any[]) => {
                // Reassign features back to context.attrsList after delay to force the ngx-datatable element to be rebuilt.
                context.attrsList = featureAttributesList;

                if (this.mapToolsService.isActive('IDENTIFY_FEATURES_WINDOW')) {
                    this.eventBus.cast('resetFeaturesInteracted');
                }

                if (context.applySorts) {
                    window.setTimeout(() => context.applySorts(), 0);
                }
            })
        );
    }

    private getFeatures(context: AttributeViewerContext): any[] {
        console.debug(`${this.title} - getFeatures`);

        const data = context.tool.featureSubTypeIds
            ? this.spatialUtilService
                  .getGraphics(context.featureLayer)
                  .filter((g: any) => context.tool.featureSubTypeIds.split(',').includes(g.attributes.type.toString()))
            : this.spatialUtilService.getGraphics(context.featureLayer);

        const featureViewModels = [];
        data.forEach((parentFeature: any) => {
            const children = [];
            const parentAttributes = structuredClone(parentFeature.attributes);

            if (context.getParentFeatureCount) {
                parentAttributes.transientId = 'parent' + parentAttributes.id;

                if (parentAttributes.totalarea) {
                    parentAttributes.totalarea = parentAttributes.totalarea.toLocaleString();
                }

                context.childrenFeatureLayers.forEach((childFeatureLayer: any) => {
                    const featureLayerType = this.spatialUtilService.getFeatureLayerName(childFeatureLayer);

                    if (parentAttributes[featureLayerType + 'ids']) {
                        this.spatialUtilService.getGraphics(childFeatureLayer).forEach((childFeature: any) => {
                            if (parentAttributes[featureLayerType + 'ids'].indexOf('[' + childFeature.attributes.id + ']') !== -1) {
                                const childAttributes = structuredClone(childFeature.attributes);

                                childAttributes.transientId = 'child' + childAttributes.id;
                                childAttributes.transientParentId = parentAttributes.transientId;

                                switch (context.featureLayer.id) {
                                    case 'lotFeatureLayer':
                                        {
                                            const polygonIds = this.spatialUtilService.parseIdsAttr(parentAttributes.polygonids);
                                            if (polygonIds.length) {
                                                const polygons = this.spatialUtilService
                                                    .getPolygons()
                                                    .filter((p: any) => polygonIds.includes(p.attributes.id));
                                                const totalArea = this.spatialUtilService.sumPolygonStatedAreas(polygons);
                                                parentAttributes.totalarea = this.spatialUtilService.toIntLocaleString(totalArea);
                                                childAttributes.totalarea = this.spatialUtilService.toFloatLocaleString(childAttributes.statedarea);
                                                childAttributes.name = this.canEditChild()
                                                    ? this.domSanitizer.bypassSecurityTrustHtml(
                                                          [
                                                              '<i title="Remove feature association"',
                                                              ' class="nlrp-icon nlrp-icon-remove-circle action-button remove-child"',
                                                              ` data-parent-id="${parentAttributes.id}"`,
                                                              ` data-child-id="${childAttributes.id}"`,
                                                              ` data-child-type="${featureLayerType}"`,
                                                              '></i>'
                                                          ].join('')
                                                      )
                                                    : '';
                                            }
                                        }
                                        break;
                                    case 'interestFeatureLayer':
                                        if (parentAttributes.subject !== undefined) {
                                            const childIcon =
                                                featureLayerType === 'polygon' || featureLayerType === 'polyline'
                                                    ? '' : featureLayerType === 'lot'
                                                      ? 'nlrp-icon-lot'
                                                      : '';
                                            childAttributes.subject =
                                                `<i class="nlrp-icon ${childIcon} child-icon"></i>` +
                                                (featureLayerType[0].toUpperCase() + featureLayerType.substring(1)) +
                                                ' (' +
                                                (childAttributes.name ? childAttributes.name : childAttributes.id) +
                                                ')';
                                            childAttributes.type = this.canEditChild()
                                                ? this.domSanitizer.bypassSecurityTrustHtml(
                                                      [
                                                          '<i title="Remove feature association"',
                                                          ' class="nlrp-icon nlrp-icon-remove-circle action-button remove-child"',
                                                          ` data-parent-id="${parentAttributes.id}"`,
                                                          ` data-child-id="${childAttributes.id}"`,
                                                          ` data-child-type="${featureLayerType}"`,
                                                          '></i>'
                                                      ].join('')
                                                  )
                                                : '';
                                        }
                                        break;
                                    case 'roadFeatureLayer':
                                        if (parentAttributes.roadid !== undefined) {
                                            const childStatedArea = parseFloat(childAttributes.statedarea);
                                            childAttributes.totalarea = childStatedArea.toLocaleString();

                                            childAttributes.roadid = this.canEditChild()
                                                ? this.domSanitizer.bypassSecurityTrustHtml(
                                                      [
                                                          '<i title="Remove feature association"',
                                                          ' class="nlrp-icon nlrp-icon-remove-circle action-button remove-child"',
                                                          ` data-parent-id="${parentAttributes.id}"`,
                                                          ` data-child-id="${childAttributes.id}"`,
                                                          ` data-child-type="${featureLayerType}"`,
                                                          '></i>'
                                                      ].join('')
                                                  )
                                                : '';
                                        }
                                        break;
                                }

                                children.push({attributes: childAttributes});
                            }
                        });
                    }
                });
            }

            featureViewModels.push({parent: {attributes: parentAttributes}, children: children});
        });

        // Combine parents and children
        return featureViewModels.reduce((acc, featureViewModel) => {
            acc.push(featureViewModel.parent, ...featureViewModel.children);
            return acc;
        }, []);
    }

    private canEditChild(): boolean {
        return this.permissionsService.hasEditPermission(['EDIT_DESIGN']);
    }

    private removeFeaturesByListType(features: any[], list: any[]) {
        console.debug(`${this.title} - removeFeaturesByListType`);

        const featureIds = features.map((feature: any) => (feature.attributes ? feature.attributes.id : feature.id));

        list.splice(
            0,
            list.length,
            ...list.filter((item: any) => {
                const attrs = item.attributes || item;
                const parentId = attrs.transientParentId ? parseInt(attrs.transientParentId.replace('parent', '')) : attrs.id;
                return featureIds.indexOf(parentId) === -1;
            })
        );
    }
}
